<template>
    <b-form class="update-connector-form" @submit.stop.prevent="onSubmit">
        <div id="type" class="group-wrapper">
            <label>Product</label>
            <div class="buttons-wrapper">
                <b-form-radio
                    v-for="product in products"
                    :key="product"
                    v-model="form.product"
                    :value="product"
                    @change="resetFormData"
                >
                    {{ product }}
                </b-form-radio>
            </div>
        </div>

        <div id="connectors" class="group-wrapper">
            <label>Choose Connector</label>
            <div class="connector-select-wrapper">
                <multiselect
                    v-model="form.connector"
                    :options="connectors"
                    :clear-on-select="false"
                    :preserve-search="true"
                    placeholder=""
                    :preselect-first="true"
                    label="name"
                    track-by="_id"
                    :show-labels="false"
                    @open="shouldHideError('connector')"
                    @select="setFormDataByConnector"
                />
                <p>{{ validationErrors.connector }}</p>
            </div>
        </div>

        <div id="is-active" class="group-wrapper">
            <label>Is Active</label>
            <b-form-checkbox v-model="form.isActive" :value="true" :unchecked-value="false" />
        </div>

        <div v-if="isEQ" id="shouldFetchOnlyActiveTreatments" class="group-wrapper">
            <label>Should fetch only active treatments</label>
            <b-form-checkbox
                v-model="form.shouldFetchOnlyActiveTreatments"
                :value="true"
                :unchecked-value="false"
            />
        </div>

        <div id="timezone" class="group-wrapper">
            <label>Time Zone</label>
            <multiselect
                v-model="form.timeZone"
                :options="timeZones"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder=""
                :show-labels="false"
                :allow-empty="true"
            />
        </div>

        <div class="group-wrapper">
            <label>IP</label>

            <div class="connector-ip-input-wrapper">
                <b-form-input id="connector-ip" v-model="form.ip" @blur="shouldHideError('ip')" />
                <p class="error">{{ validationErrors.ip }}</p>
            </div>
        </div>

        <div v-if="isVitro" id="focals" class="group-wrapper">
            <label>Focals</label>
            <div class="focals-input-wrapper">
                <b-form-input
                    id="focals"
                    v-model="form.focals"
                    :placeholder="focalsPlaceholder"
                    :required="true"
                    @focus="shouldHideError('focals')"
                />
                <p>{{ validationErrors.focals }}</p>
            </div>
        </div>

        <!-- LAB API SETTINGS -->
        <b-card no-body>
            <b-card-header v-b-toggle.lab-api class="group-wrapper">
                <label>Lab API </label>
                <b-icon-plus-lg />
            </b-card-header>
            <b-collapse id="lab-api">
                <b-card-body>
                    <div id="url" class="group-wrapper">
                        <label>URL</label>
                        <b-form-input id="url" v-model="form.labApi.url" />
                    </div>

                    <div id="user-name" class="group-wrapper">
                        <label>User Name</label>
                        <b-form-input id="user" v-model="form.labApi.user" />
                    </div>

                    <div id="password" class="group-wrapper">
                        <label>Password</label>
                        <b-form-input id="password" v-model="form.labApi.password" />
                    </div>
                </b-card-body>
            </b-collapse>
        </b-card>

        <!-- CONNECTION SETTINGS -->
        <b-card no-body>
            <b-card-header v-b-toggle.connection-settings class="group-wrapper">
                <label>Connection Settings </label>
                <b-icon-plus-lg />
            </b-card-header>
            <b-collapse id="connection-settings">
                <b-card-body>
                    <div id="maxRequestsPerSecond" class="group-wrapper">
                        <label>Max Requests Per Second</label>
                        <b-form-input
                            id="maxRequestsPerSecond"
                            v-model="form.labApi.connectionSettings.maxRequestsPerSecond"
                            type="number"
                            min="0"
                        />
                    </div>

                    <div id="imagesRequestTimeoutInMs" class="group-wrapper">
                        <label>Images Request Timeout (ms)</label>
                        <b-form-input
                            id="imagesRequestTimeoutInMs"
                            v-model="form.labApi.connectionSettings.imagesRequestTimeoutInMs"
                            type="number"
                            min="0"
                        />
                    </div>

                    <div id="metadataRequestTimeoutInMs" class="group-wrapper">
                        <label>Metadata Request Timeout (ms)</label>
                        <b-form-input
                            id="metadataRequestTimeoutInMs"
                            v-model="form.labApi.connectionSettings.metadataRequestTimeoutInMs"
                            type="number"
                            min="0"
                        />
                    </div>

                    <div id="socketTimeoutInMs" class="group-wrapper">
                        <label>Socket Timeout (ms)</label>
                        <b-form-input
                            id="socketTimeoutInMs"
                            v-model="form.labApi.connectionSettings.socketTimeoutInMs"
                            type="number"
                            min="0"
                        />
                    </div>

                    <div id="idleSocketTimeoutInMs" class="group-wrapper">
                        <label>Idle Socket Timeout (ms)</label>
                        <b-form-input
                            id="idleSocketTimeoutInMs"
                            v-model="form.labApi.connectionSettings.idleSocketTimeoutInMs"
                            type="number"
                            min="0"
                        />
                    </div>

                    <div id="maxOpenSockets" class="group-wrapper">
                        <label>Max Open Sockets</label>
                        <b-form-input
                            id="maxOpenSockets"
                            v-model="form.labApi.connectionSettings.maxOpenSockets"
                            type="number"
                            min="0"
                        />
                    </div>

                    <div id="maxRetries" class="group-wrapper">
                        <label>Max Retries</label>
                        <b-form-input
                            id="maxRetries"
                            v-model="form.labApi.connectionSettings.maxRetries"
                            type="number"
                            min="0"
                        />
                    </div>
                </b-card-body>
            </b-collapse>
        </b-card>

        <div id="submit" class="group-wrapper">
            <b-button type="submit" variant="primary">Submit</b-button>
        </div>
    </b-form>
</template>

<script>
import Multiselect from 'vue-multiselect'
import Utils from '../../../../business/utils/Utils'
import UIUtils from '../../../../business/utils/UIUtils'
import BackendProvider from '../../../../business/services/BackendProvider'
import { TIMEZONES } from '../../../../business/services/timezones'
import { INSTRUMENT_TYPE, PRODUCTS } from '../../../../business/const'

export default {
    name: 'UpdateConnectorForm',
    components: { Multiselect },
    data() {
        return {
            products: [PRODUCTS.EQ, PRODUCTS.OQ],
            form: {
                product: PRODUCTS.EQ,
                connector: '',
                isActive: false,
                shouldFetchOnlyActiveTreatments: true,
                timeZone: '',
                ip: '',
                focals: '',
                connectorType: '',
                labApi: {
                    url: '',
                    user: '',
                    password: '',
                    connectionSettings: {
                        maxRequestsPerSecond: '',
                        imagesRequestTimeoutInMs: '',
                        metadataRequestTimeoutInMs: '',
                        socketTimeoutInMs: '',
                        idleSocketTimeoutInMs: '',
                        maxOpenSockets: '',
                        maxRetries: ''
                    }
                }
            },
            OQConnectors: [],
            EQConnectors: [],
            timeZones: Object.values(TIMEZONES),
            validationErrors: {
                focals: '',
                connector: '',
                ip: ''
            },
            validationMessages: {
                MUST_SELECT: 'Please select a value',
                NO_RESPONSE: 'No response from lab API URL',
                INVALID_CREDENTIALS: 'Invalid lab API credentials',
                INVALID_FOCALS: 'Focals must be numbers between -5 and 5',
                INVALID_IP: 'Please, enter valid IP'
            },
            focalsPlaceholder: 'Comma seperated values -1,0,1'
        }
    },
    computed: {
        isOQ() {
            return this.form.product === PRODUCTS.OQ
        },
        isEQ() {
            return this.form.product === PRODUCTS.EQ
        },
        focalsArray() {
            return this.form.focals.split(',').map(focal => Number(focal))
        },
        isInputValid() {
            return Object.values(this.validationErrors).every(value =>
                Utils.isNullOrEmptyString(value)
            )
        },
        isVitro() {
            return this.form.connectorType === INSTRUMENT_TYPE.VITRO
        },
        shouldTestLabApiConnection() {
            return this.form.labApi.url || this.form.labApi.user || this.form.labApi.password
        },
        isConnectionSettingsEmpty() {
            return Object.values(this.form.labApi.connectionSettings).every(value =>
                Utils.isNullOrEmptyString(value)
            )
        },
        connectors() {
            if (this.isOQ) return this.OQConnectors
            return this.EQConnectors
        }
    },
    mounted() {
        this.loadConnectors()
    },
    methods: {
        async onSubmit() {
            UIUtils.showLoadingMask()
            this.validateInputFields()
            if (this.isInputValid) {
                try {
                    const updatedConnector = await this.updateConnector()
                    UIUtils.toast(`Connector ${updatedConnector._id} updated`, 'message', 10 * 1000)
                    this.$bvModal.hide('update_connector')
                } catch (e) {
                    this.handleValidationError(e.message)
                }
            }
            UIUtils.hideLoadingMask()
        },
        validateInputFields() {
            if (!this.form.connector)
                this.validationErrors.connector = this.validationMessages.MUST_SELECT

            this.validationErrors.ip = ''
            if (this.form.ip && !this.isValidIp(this.form.ip)) {
                this.validationErrors.ip = this.validationMessages.INVALID_IP
            }

            const focals = this.focalsArray
            const isFocalsValid = focals.every(
                focal => typeof focal === 'number' && focal <= 5 && focal >= -5
            )
            if (!isFocalsValid)
                this.validationErrors.focals = this.validationMessages.INVALID_FOCALS
        },
        async updateConnector() {
            if (this.shouldTestLabApiConnection) await this.testConnection()

            return await BackendProvider.updateConnector(
                this.form.connector._id,
                this.getUpdateConnectorObject(),
                this.form.product
            )
        },
        async loadConnectors() {
            BackendProvider.getAllConnectors(PRODUCTS.EQ).then(
                connectors => (this.EQConnectors = connectors)
            )
            BackendProvider.getAllConnectors(PRODUCTS.OQ).then(
                connectors => (this.OQConnectors = connectors)
            )
        },
        async testConnection() {
            const product = this.form.product
            const { connectorType, vendor } = this.form.connector

            const {
                url: oldUrl,
                user: oldUserName,
                password: oldPassword,
                version
            } = this.form.connector.labApi

            const { url: newUrl, user: newUserName, password: newPassword } = this.form.labApi

            const labApi = {
                url: newUrl || oldUrl,
                user: newUserName || oldUserName,
                password: newPassword || oldPassword,
                version
            }
            const shouldDecrypt = !newPassword ? true : undefined;
            const testConnectionDto = {
                labApi,
                shouldDecrypt
            }
            if (product === PRODUCTS.EQ) {
                testConnectionDto.connectorType = connectorType
            } else {
                testConnectionDto.vendor = vendor
            }
            return await BackendProvider.testConnection(testConnectionDto, product)
        },
        handleValidationError(message) {
            switch (message) {
                case this.validationMessages.INVALID_CREDENTIALS:
                    UIUtils.toast(
                        this.$translate('lab_api_invalid_credentials'),
                        'danger',
                        10 * 1000
                    )
                    break
                case this.validationMessages.NO_RESPONSE:
                    UIUtils.toast(this.$translate('lab_api_no_response'), 'danger', 10 * 1000)
                    break
                default:
                    UIUtils.toast(message, 'danger', 10 * 1000)
            }
        },
        shouldHideError(id) {
            if (this.validationErrors[id]) this.validationErrors[id] = ''
        },
        getUpdateConnectorObject() {
            return {
                ...(!Utils.isNullOrEmptyString(this.form.isActive) && {
                    isActive: this.form.isActive
                }),
                ...(!Utils.isNullOrEmptyString(this.form.shouldFetchOnlyActiveTreatments) && {
                    shouldFetchOnlyActiveTreatments: this.form.shouldFetchOnlyActiveTreatments
                }),
                ...(!Utils.isNullOrEmptyString(this.form.timeZone) && {
                    timeZone: this.form.timeZone
                }),
                ...(!Utils.isNullOrEmptyString(this.form.timeZone) && {
                    ip: this.form.ip
                }),
                ...(this.isEQ &&
                    !Utils.isNullOrEmptyString(this.focalsArray) && {
                        focals: this.focalsArray
                    }),
                ...this.getUpdateLabApiObject()
            }
        },
        getUpdateLabApiObject() {
            const { url, user, password } = this.form.labApi

            const { isNullOrEmptyString: isUntouched } = Utils

            return {
                labApi: {
                    ...(!isUntouched(url) && { url }),
                    ...(!isUntouched(user) && { user }),
                    ...(!isUntouched(password) && { password }),
                    ...(!this.isConnectionSettingsEmpty && this.getUpdateConnectionSettingsObject())
                }
            }
        },
        getUpdateConnectionSettingsObject() {
            const {
                maxRequestsPerSecond,
                imagesRequestTimeoutInMs,
                metadataRequestTimeoutInMs,
                socketTimeoutInMs,
                idleSocketTimeoutInMs,
                maxOpenSockets,
                maxRetries
            } = this.form.labApi.connectionSettings

            const { isNullOrEmptyString: isUntouched } = Utils

            return {
                connectionSettings: {
                    ...(!isUntouched(maxRequestsPerSecond) && {
                        maxRequestsPerSecond: Number(maxRequestsPerSecond)
                    }),
                    ...(!isUntouched(imagesRequestTimeoutInMs) && {
                        imagesRequestTimeoutInMs: Number(imagesRequestTimeoutInMs)
                    }),
                    ...(!isUntouched(metadataRequestTimeoutInMs) && {
                        metadataRequestTimeoutInMs: Number(metadataRequestTimeoutInMs)
                    }),
                    ...(!isUntouched(socketTimeoutInMs) && {
                        socketTimeoutInMs: Number(socketTimeoutInMs)
                    }),
                    ...(!isUntouched(idleSocketTimeoutInMs) && {
                        idleSocketTimeoutInMs: Number(idleSocketTimeoutInMs)
                    }),
                    ...(!isUntouched(maxOpenSockets) && {
                        maxOpenSockets: Number(maxOpenSockets)
                    }),
                    ...(!isUntouched(maxRetries) && {
                        maxRetries: Number(maxRetries)
                    })
                }
            }
        },
        resetFormData(product) {
            this.form = {
                product: product,
                connector: null,
                isActive: false,
                shouldFetchOnlyActiveTreatments: true,
                timeZone: '',
                ip: '',
                focals: '',
                connectorType: '',
                labApi: {
                    url: '',
                    user: '',
                    password: '',
                    connectionSettings: {
                        maxRequestsPerSecond: '',
                        imagesRequestTimeoutInMs: '',
                        metadataRequestTimeoutInMs: '',
                        socketTimeoutInMs: '',
                        idleSocketTimeoutInMs: '',
                        maxOpenSockets: '',
                        maxRetries: ''
                    }
                }
            }
        },
        setFormDataByConnector(connector) {
            this.form.isActive = connector.isActive
            this.form.shouldFetchOnlyActiveTreatments = connector.shouldFetchOnlyActiveTreatments
            this.form.timeZone = connector.timeZone
            this.form.ip = connector.ip
            this.form.connectorType = connector.connectorType
            this.form.focals = connector.focals ? connector.focals.join() : ''

            this.form.labApi.url = connector.labApi.url
            this.form.labApi.user = connector.labApi.user
            this.form.labApi.password = connector.labApi.password
            this.form.labApi.version = connector.labApi.version

            if (connector.labApi.connectionSettings) {
                this.form.labApi.connectionSettings.maxRequestsPerSecond =
                    connector.labApi.connectionSettings.maxRequestsPerSecond
                this.form.labApi.connectionSettings.imagesRequestTimeoutInMs =
                    connector.labApi.connectionSettings.imagesRequestTimeoutInMs
                this.form.labApi.connectionSettings.metadataRequestTimeoutInMs =
                    connector.labApi.connectionSettings.metadataRequestTimeoutInMs
                this.form.labApi.connectionSettings.socketTimeoutInMs =
                    connector.labApi.connectionSettings.socketTimeoutInMs
                this.form.labApi.connectionSettings.idleSocketTimeoutInMs =
                    connector.labApi.connectionSettings.idleSocketTimeoutInMs
                this.form.labApi.connectionSettings.maxOpenSockets =
                    connector.labApi.connectionSettings.maxOpenSockets
                this.form.labApi.connectionSettings.maxRetries =
                    connector.labApi.connectionSettings.maxRetries
            } else {
                this.form.labApi.connectionSettings = {
                    maxRequestsPerSecond: '',
                    imagesRequestTimeoutInMs: '',
                    metadataRequestTimeoutInMs: '',
                    socketTimeoutInMs: '',
                    idleSocketTimeoutInMs: '',
                    maxOpenSockets: '',
                    maxRetries: ''
                }
            }
            console.log(this.form)
        },
        isValidIp(ip) {
            // https://medium.com/@stheodorejohn/validating-ipv4-and-ipv6-addresses-with-ease-unveiling-the-power-of-validation-in-javascript-2af04ee065c5#:~:text=To%20validate%20an%20IP%20address,1%20to%204%20hexadecimal%20digits.
            const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/
            const ipv6Regex = /^([\da-f]{1,4}:){7}[\da-f]{1,4}$/i

            if (ipv4Regex.test(ip)) {
                return ip.split('.').every(part => parseInt(part) <= 255)
            }
            if (ipv6Regex.test(ip)) {
                return ip.split(':').every(part => part.length <= 4)
            }
            return false
        }
    }
}
</script>

<style lang="scss" scoped>
.update-connector-form {
    position: relative;
    width: 30rem;

    .divider {
        padding: 0.5rem;
        color: $light;
    }

    .group-wrapper {
        position: relative;
        display: grid;
        grid-template-columns: 40% 60%;
        align-items: center;
        padding: 0.5rem;
        min-height: 4.5rem;

        label {
            padding-top: 3%;
        }

        p {
            color: $red;
            font-size: 0.9rem;
            margin: 0;
            padding-top: 0.1rem;
        }

        .connector-select-wrapper {
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            padding-top: 0.5rem;
        }

        .buttons-wrapper {
            display: flex;
            justify-content: space-around;
        }
    }

    .card {
        .card-header.group-wrapper {
            cursor: pointer;
            border: unset;
            background-color: $dark-2;
            display: flex;
            justify-content: flex-start;
            align-items: center;

            label {
                cursor: unset;
                margin-bottom: unset;
                padding-bottom: 0.5rem;
            }

            .bi-plus-lg.b-icon.bi {
                padding-top: 0.25rem;
                padding-left: 0.75rem;
                font-size: 0.8rem;
                width: unset;
                height: unset;
            }
        }

        .card-body {
            background-color: $dark-2;
        }

        #connection-settings {
            .card-body {
                .group-wrapper {
                    grid-template-columns: 70% 30%;
                }
            }
        }
    }

    #submit.group-wrapper {
        display: flex;
        justify-content: center;
    }
}
</style>

<!--override vue multiselect component styles-->
<style lang="scss">
#timezone.group-wrapper,
#connectors.group-wrapper {
    .multiselect {
        z-index: 2;
        font-size: 0.9rem;
        position: relative;

        .multiselect__tags {
            background-color: $dark-2;
            border-radius: 0.3rem;
            border-color: $light;

            .multiselect__single {
                color: $light-2;
                background-color: transparent;
                font-size: 0.9rem;
            }

            .multiselect__placeholder {
                font-size: 0.9rem;
            }

            .multiselect__input {
                background-color: $dark-2;
                color: $light;
                font-size: 0.9rem;
            }
        }

        .multiselect__content-wrapper {
            position: absolute;
            max-height: 10rem;
            background-color: $dark-2;
            border-color: $light;
            border-radius: 0.3rem;

            .multiselect__content {
                background-color: $dark-2;
                color: $light-2;
                max-height: 10rem;

                .multiselect__element {
                    .multiselect__option--highlight {
                        background-color: $blue;
                    }
                    .multiselect__option--selected {
                        background-color: $blue;
                    }
                }
            }
        }
    }

    .multiselect--active {
        position: absolute;
        top: 1rem;
        left: 12.1rem;
        width: 17.43rem;
        height: 100%;
    }
}
#connectors.group-wrapper {
    .multiselect {
        z-index: 4;
    }
}
#timezone.group-wrapper {
    .multiselect {
        z-index: 3;
    }
    .multiselect--active {
        top: 0.87rem;
    }
}
</style>
